/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { OperationalFilterSearchField, OperationalFilterPickerRow, OperationalFilterFieldLabel, OperationalFilterSectionDivider } from '../../../../shared/filters/OperationalFilterPrimitives'
import { MasterFilterCompactPicker } from '../../../../shared/filters/MasterFilterCompactPicker'
import { MasterFilterSheetModal } from '../../../../shared/filters/MasterFilterSheetModal'
import type { MasterFilterOption } from '../../../../shared/filters/MasterFilterSheetModal'
import { FilterSheetButton } from '../../../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../../../shared/filters/FilterSheetModal'
import { useOperationalFilterLayout } from '../../../../shared/filters/useOperationalFilterLayout'
import { operationalFilterStyles as filterStyles } from '../../../../shared/filters/operationalFilters.styles';
import { ticketOperationalHeader as oh } from '../../../../shared/ticket/ticketOperationalHeader.styles'
import { ticketColors } from '../../../../shared/theme/ticketColors';
import { spacing } from '../../../../shared/theme/spacing';
import type { SortMode, ViewMode } from './useEmployeePermissions';

type Props = {
  searchQuery: string;
  onSearchChange: (text: string) => void;
  branchFilter: string | null;
  onBranchFilterChange: (id: string | null) => void;
  branchOptions: MasterFilterOption[];
  sortBy: SortMode;
  onSortChange: (mode: SortMode) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  showViewToggle: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

const SORT_OPTIONS: { id: SortMode; name: string }[] = [
  { id: 'name-asc', name: 'Nombre (A-Z)' },
  { id: 'name-desc', name: 'Nombre (Z-A)' },
  { id: 'date-asc', name: 'Fecha de alta' },
  { id: 'branches-desc', name: 'Más sucursales' },
];

const VIEW_OPTIONS: { id: ViewMode; name: string }[] = [
  { id: 'cards', name: 'Tarjetas' },
  { id: 'matrix', name: 'Matriz' },
];

const DEFAULT_SORT: SortMode = 'name-asc';

export function EmployeePermissionsToolbar({
  searchQuery,
  onSearchChange,
  branchFilter,
  onBranchFilterChange,
  branchOptions,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  showViewToggle,
  hasActiveFilters,
  onClearFilters,
}: Props) {
  const { isMobile, layoutWide } = useOperationalFilterLayout();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [viewSheetOpen, setViewSheetOpen] = useState(false);

  const branchLabel = useMemo(
    () => branchOptions.find((b) => b.id === branchFilter)?.name ?? 'Todas',
    [branchOptions, branchFilter]
  );
  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((s) => s.id === sortBy)?.name ?? 'Nombre (A-Z)',
    [sortBy]
  );
  const viewLabel = useMemo(
    () => VIEW_OPTIONS.find((v) => v.id === viewMode)?.name ?? 'Tarjetas',
    [viewMode]
  );

  const activeFilterCount =
    (branchFilter ? 1 : 0) +
    (sortBy !== DEFAULT_SORT ? 1 : 0) +
    (viewMode !== 'cards' && showViewToggle ? 1 : 0);

  const sheetModals = (
    <>
      <MasterFilterSheetModal
        visible={branchSheetOpen}
        title="Filtrar por sucursal"
        options={branchOptions}
        selectedId={branchFilter}
        onSelect={onBranchFilterChange}
        onClose={() => setBranchSheetOpen(false)}
        allOptionLabel="Todas las sucursales"
      />
      <MasterFilterSheetModal
        visible={sortSheetOpen}
        title="Ordenar por"
        options={SORT_OPTIONS}
        selectedId={sortBy}
        onSelect={(id) => id && onSortChange(id as SortMode)}
        onClose={() => setSortSheetOpen(false)}
        showAllOption={false}
      />
      {showViewToggle ? (
        <MasterFilterSheetModal
          visible={viewSheetOpen}
          title="Modo de vista"
          options={VIEW_OPTIONS}
          selectedId={viewMode}
          onSelect={(id) => id && onViewModeChange(id as ViewMode)}
          onClose={() => setViewSheetOpen(false)}
          showAllOption={false}
        />
      ) : null}
    </>
  );

  const filterFields = (
    <View style={{ gap: spacing.sm, alignSelf: 'stretch' }}>
      <OperationalFilterPickerRow
        label="Sucursal"
        valueLabel={branchLabel}
        onPress={() => setBranchSheetOpen(true)}
        active={branchFilter !== null}
        accessibilityLabel="Filtrar por sucursal"
      />
      <OperationalFilterPickerRow
        label="Orden"
        valueLabel={sortLabel}
        onPress={() => setSortSheetOpen(true)}
        active={sortBy !== DEFAULT_SORT}
        accessibilityLabel="Ordenar"
      />
      {showViewToggle ? (
        <OperationalFilterPickerRow
          label="Vista"
          valueLabel={viewLabel}
          onPress={() => setViewSheetOpen(true)}
          active={viewMode !== 'cards'}
          accessibilityLabel="Cambiar vista"
        />
      ) : null}
    </View>
  );

  if (isMobile) {
    return (
      <View style={styles.mobileToolbar}>
        <OperationalFilterSearchField
          value={searchQuery}
          onChangeText={onSearchChange}
          placeholder="Buscar por nombre, email o rol"
          autoCapitalize="none"
          testID="perfil.team.permissions.search"
        />
        <FilterSheetButton
          variant="ticket"
          activeCount={activeFilterCount}
          onOpen={() => setFiltersOpen(true)}
          accessibilityLabel="Abrir filtros de empleados"
        />
        <FilterSheetModal
          visible={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={() => setFiltersOpen(false)}
          onClear={onClearFilters}
          hasActiveDraft={hasActiveFilters || activeFilterCount > 0}
          title="Filtros"
          appearance="ticket"
        >
          {filterFields}
        </FilterSheetModal>
        {sheetModals}
      </View>
    );
  }

  return (
    <>
      <View style={styles.toolbarDesktop}>
        <View style={filterStyles.wideToolbarRow}>
          <View style={[filterStyles.wideToolbarGroup, { flex: 1, minWidth: 220 }]}>
            <OperationalFilterFieldLabel>Buscar</OperationalFilterFieldLabel>
            <OperationalFilterSearchField
              value={searchQuery}
              onChangeText={onSearchChange}
              placeholder="Buscar por nombre, email o rol"
              autoCapitalize="none"
              appearance="desk"
              testID="perfil.team.permissions.search"
            />
          </View>
          {layoutWide ? (
            <>
              <View style={filterStyles.wideToolbarGroup}>
                <MasterFilterCompactPicker
                  caption="Sucursal"
                  value={branchLabel}
                  active={branchFilter !== null}
                  dense
                  onPress={() => setBranchSheetOpen(true)}
                />
              </View>
              <View style={filterStyles.wideToolbarGroup}>
                <MasterFilterCompactPicker
                  caption="Orden"
                  value={sortLabel}
                  active={sortBy !== DEFAULT_SORT}
                  dense
                  onPress={() => setSortSheetOpen(true)}
                />
              </View>
              {showViewToggle ? (
                <View style={filterStyles.wideToolbarGroup}>
                  <MasterFilterCompactPicker
                    caption="Vista"
                    value={viewLabel}
                    active={viewMode !== 'cards'}
                    dense
                    onPress={() => setViewSheetOpen(true)}
                  />
                </View>
              ) : null}
            </>
          ) : (
            <View style={oh.deskActionRail}>
              <FilterSheetButton
                variant="desk"
                activeCount={activeFilterCount}
                onOpen={() => setFiltersOpen(true)}
                accessibilityLabel="Abrir filtros de empleados"
              />
            </View>
          )}
        </View>
      </View>
      {!layoutWide ? (
        <FilterSheetModal
          visible={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={() => setFiltersOpen(false)}
          onClear={onClearFilters}
          hasActiveDraft={hasActiveFilters || activeFilterCount > 0}
          title="Filtros"
          appearance="ticket"
        >
          <OperationalFilterSectionDivider />
          {filterFields}
        </FilterSheetModal>
      ) : null}
      {sheetModals}
    </>
  );
}

const styles = StyleSheet.create({
  mobileToolbar: {
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  toolbarDesktop: {
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
});
